import { compareInventoryRows, formatInventoryNumber, isInventorySoldOut, matchesInventorySearch } from "../utils/inventoryDisplay";
import { englishError } from "../utils/databaseErrors";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../supabaseClient";
import BlockForm from "./BlockForm";
import { PRODUCT_TYPES } from "../utils/stock";
import { sumQuantities } from "../utils/marbleQuantity";
import './BlockTable.css';

// Popup Modal reusable component
function PopupModal({ children, onClose }) {
  return (
    <div className="ui-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(81,69,54,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2001,
      }}
      onClick={onClose}
    >
      <div className="ui-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#faf8f4",
          borderRadius: 13,
          minWidth: 420,
          maxWidth: "97vw",
          padding: 28,
          boxShadow: "0 0 32px #51453655",
          direction: "ltr",
          maxHeight: "94vh",
          overflowY: "auto",
        }}
      >
        {children}
      </div>
    </div>
  );
}

const treatMap = { epoxy: "Epoxy", resin: "Resin", none: "None" };

function BlockTable({ user }) {
  const [blocks, setBlocks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [editBlock, setEditBlock] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');
  const [selMaterial, setSelMaterial] = useState('all');
  const [search, setSearch] = useState('');
  const deletingRef = useRef(false);

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setLoadError('');
    try {
      async function allRows(table) {
        const rows = [];
        for (let start = 0; ; start += 1000) {
          const { data, error: queryError } = await supabase.from(table).select('*').order('id', { ascending: false }).range(start, start + 999);
          if (queryError) throw queryError;
          rows.push(...(data || []));
          if (!data || data.length < 1000) return rows;
        }
      }
      const [blks, cust, mats, dists] = await Promise.all(['block', 'customer', 'material', 'block_distribution'].map(allRows));
      setBlocks(blks); setCustomers(cust); setMaterials(mats); setDistributions(dists);
      setSelectedIds(current => new Set([...current].filter(id => blks.some(b => String(b.id) === id))));
    } catch (err) {
      setLoadError('Unable to load products: ' + englishError(err.message));
    } finally { setLoading(false); }
  }

  function getCustomerName(id) {
    const c = customers.find((x) => String(x.id) === String(id));
    return c ? c.name : "-";
  }

  function getMaterialName(id) {
    const m = materials.find((x) => String(x.id) === String(id));
    return m ? m.material_name || m.name : "-";
  }

  async function handleDelete(blockIds) {
    if (deletingRef.current || loading || loadError) return;
    const ids = [...new Set(blockIds.map(Number))].sort((a, b) => a - b);
    if (!ids.length || ids.some(id => !Number.isSafeInteger(id) || id <= 0)) { setError('Select at least one valid product.'); return; }
    const prompt = ids.length === 1 ? 'Delete this product and its showroom stock? This cannot be undone.' : `Delete ${ids.length} selected products and their showroom stock? This cannot be undone.`;
    if (!window.confirm(prompt)) return;
    deletingRef.current = true; setDeleting(true); setError(''); setMessage('');
    try {
      const { data, error: deleteError } = await supabase.rpc('delete_blocks_tx', { p_block_ids: ids });
      if (deleteError) throw deleteError;
      setSelectedIds(current => new Set([...current].filter(id => !ids.includes(Number(id)))));
      setMessage(`${data} product${Number(data) === 1 ? '' : 's'} deleted.`);
      await fetchAll();
    } catch (err) {
      setError('Unable to delete products: ' + englishError(err.message));
    } finally { deletingRef.current = false; setDeleting(false); }
  }

  function toggleSelection(id) {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // ✅ صف واحد لكل منتج — نجمع القطع عبر كل التوزيعات
  const rows = useMemo(() => {
    const distByBlockId = new Map();
    (distributions || []).forEach((d) => {
      const blockId = Number(d.block_id);
      if (!Number.isFinite(blockId)) return;
      if (!distByBlockId.has(blockId)) distByBlockId.set(blockId, []);
      distByBlockId.get(blockId).push(d);
    });

    const formatTreatLabel = (block, blockDists) => {
      const treatSet = new Set(
        blockDists.map((d) => {
          const face = treatMap[d.face_treatment || "none"];
          const back = treatMap[d.back_treatment || "none"];
          const pol = d.is_polished ? "Polished" : "Unpolished";
          return `Front: ${face} | Back: ${back} | ${pol}`;
        })
      );

      if (treatSet.size === 1) {
        return [...treatSet][0];
      }
      if (treatSet.size > 1) {
        return "Multiple";
      }

      const face = treatMap[block.face_treatment || "none"];
      const back = treatMap[block.back_treatment || "none"];
      const pol = block.is_polished ? "Polished" : "Unpolished";
      return `Front: ${face} | Back: ${back} | ${pol}`;
    };

    const groupedByCode = new Map();

    (blocks || []).forEach((b) => {
      const key = String(b.id);
      let entry = groupedByCode.get(key);
      if (!entry) {
        entry = {
          rowId: String(key),
          block_id: b.id,
          blockIds: [],
          code: b.code,
          entry_date: b.entry_date,
          notes: b.notes_text,
          product_type: b.product_type || "slab",
          customer_id: b.customer_id,
          material_id: b.material_id,
          width: b.width,
          height: b.height,
          thickness: b.thickness,
          slabs: 0,
          area: 0,
          price: b.m_price,
          treatSet: new Set(),
        };
        groupedByCode.set(key, entry);
      }

      entry.blockIds.push(b.id);

      const blockDists = distByBlockId.get(b.id) || [];
      const totalSlabsFromDists = sumQuantities(blockDists.map(d => d.slabs_count));
      const blockSlabs =
        blockDists.length > 0
          ? totalSlabsFromDists
          : Number(b.slabs_number || 0) || 0;

      entry.slabs += blockSlabs;
      entry.area += (Number(b.width) || 0) * (Number(b.height) || 0) * blockSlabs;

      if (entry.price == null && b.m_price != null) {
        entry.price = b.m_price;
      }
      if (!entry.width && b.width) entry.width = b.width;
      if (!entry.height && b.height) entry.height = b.height;
      if (!entry.thickness && b.thickness) entry.thickness = b.thickness;
      if (entry.customer_id == null && b.customer_id != null) {
        entry.customer_id = b.customer_id;
      }
      if (entry.material_id == null && b.material_id != null) {
        entry.material_id = b.material_id;
      }

      entry.treatSet.add(formatTreatLabel(b, blockDists));
    });

    return Array.from(groupedByCode.values()).map((entry) => {
      let treatLabel = "-";
      if (entry.treatSet.size === 1) {
        treatLabel = [...entry.treatSet][0];
      } else if (entry.treatSet.size > 1) {
        treatLabel = "Multiple";
      }

      return {
        rowId: entry.rowId,
        block_id: entry.block_id,
        blockIds: entry.blockIds,
        code: entry.code,
        shownCode: entry.code,
        entryDate: entry.entry_date,
        notes: entry.notes,
        productType: PRODUCT_TYPES[entry.product_type],
        customer: getCustomerName(entry.customer_id),
        material: getMaterialName(entry.material_id),
        materialId: entry.material_id,
        width: entry.width,
        height: entry.height,
        thickness: entry.thickness,
        slabs: entry.slabs,
        area: entry.area,
        price: entry.price,
        treat: treatLabel,
      };
    }).sort(compareInventoryRows);
  }, [blocks, distributions, customers, materials]);
  const materialOptions = useMemo(() => Array.from(new Map(rows.map(row => [String(row.materialId), { id: String(row.materialId), name: row.material }])).values()), [rows]);
  const visibleRows = useMemo(() => rows.filter(row =>
    (selMaterial === 'all' || String(row.materialId) === selMaterial) && matchesInventorySearch(row, search)
  ), [rows, selMaterial, search]);
  const visibleSelectedIds = visibleRows.filter(row => selectedIds.has(String(row.block_id))).map(row => String(row.block_id));
  function changeFilter(setFilter, value) {
    setFilter(value);
    setSelectedIds(new Set());
  }
  const selectionDisabled = loading || deleting || !!loadError;

  // ستايل الجدول
  const thStyle = {
    padding: "6px 8px",
    borderBottom: "1.5px solid #d2cbc1",
    background: "#f2ede4",
    fontWeight: "bold",
    fontSize: 13,
    textAlign: "left",
    whiteSpace: "normal",
  };

  const tdStyle = {
    padding: "6px 8px",
    fontSize: 13,
    textAlign: "left",
    verticalAlign: "top",
    background: "#faf8f4",
  };

  return (
    <div
      className="block-catalog ui-legacy-page"
      dir="ltr"
      style={{
        textAlign: "left",
        fontFamily: "Jost, 'Trebuchet MS', Arial, sans-serif",
        background: "#f2ede4",
        minHeight: "100vh",
        maxWidth: 1100,
        margin: "auto",
        padding: 24,
      }}
    >
      <div className="ui-heading-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0, color: "var(--accent, #514536)" }}>Products</h2>
        <button className="ui-primary"
          disabled={deleting}
          onClick={() => {
            setEditBlock(null);
            setShowPopup(true);
          }}
          style={{
            padding: "8px 16px",
            backgroundColor: "var(--accent, #514536)",
            color: "#faf8f4",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Add product
        </button>
      </div>

      <div className="ui-toolbar">
        <label className="ui-field">Material
          <select value={selMaterial} disabled={deleting} onChange={event => changeFilter(setSelMaterial, event.target.value)}>
            <option value="all">All materials</option>
            {materialOptions.map(material => <option key={material.id} value={material.id}>{material.name}</option>)}
          </select>
        </label>
        <label className="ui-field">Search material or block code
          <input type="search" value={search} disabled={deleting} onChange={event => changeFilter(setSearch, event.target.value)} />
        </label>
        <button className="ui-secondary" type="button" disabled={deleting} onClick={() => { setSelMaterial('all'); setSearch(''); setSelectedIds(new Set()); }}>Reset</button>
      </div>
      <div className="block-selection-toolbar">
        <button className="ui-secondary" type="button" disabled={selectionDisabled || !visibleRows.length} onClick={() => setSelectedIds(new Set(visibleRows.map(row => String(row.block_id))))}>Select all</button>
        <button className="ui-secondary" type="button" disabled={selectionDisabled || !visibleSelectedIds.length} onClick={() => setSelectedIds(new Set())}>Clear selection</button>
        <span role="status">{visibleSelectedIds.length} selected</span>
        <button type="button" className="block-delete-selected ui-primary" disabled={selectionDisabled || !visibleSelectedIds.length} onClick={() => handleDelete(visibleSelectedIds)}>{deleting ? 'Deleting…' : `Delete selected (${visibleSelectedIds.length})`}</button>
      </div>
      {error && <p role="alert">{error}</p>}
      {loadError && <div><p role="alert">{loadError}</p><button className="ui-secondary" type="button" onClick={fetchAll} disabled={loading}>Retry loading</button></div>}
      {message && <p role="status">{message}</p>}
      {loading && <p role="status">Loading products…</p>}

      <div className="block-table-scroll">
      <div className="ui-table-scroll"><table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          background: "#faf8f4",
          borderRadius: 12,
          boxShadow: "0 1px 10px #51453610",
          direction: "ltr",
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ background: "#f2ede4", color: "var(--accent, #514536)" }}>
            <th style={thStyle}><input type="checkbox" aria-label="Select all products" disabled={selectionDisabled || !visibleRows.length} checked={visibleRows.length > 0 && visibleSelectedIds.length === visibleRows.length} ref={node => { if (node) node.indeterminate = visibleSelectedIds.length > 0 && visibleSelectedIds.length < visibleRows.length; }} onChange={event => setSelectedIds(event.target.checked ? new Set(visibleRows.map(row => String(row.block_id))) : new Set())} /></th>
            <th style={thStyle}>No.</th>
            <th style={thStyle}>Code</th>
            <th style={thStyle}>Entry date</th>
            <th style={thStyle}>Product type</th>
            <th style={thStyle}>Supplier</th>
            <th style={thStyle}>Material</th>
            <th style={thStyle}>Width</th>
            <th style={thStyle}>Height</th>
            <th style={thStyle}>Thickness</th>
            <th style={thStyle}>Quantity (Total)</th>
            <th style={thStyle}>Area (m²)</th>
            <th style={thStyle}>Supplier price</th>
            <th style={thStyle}>Treatment</th>
            <th style={thStyle}>Notes</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => (
            <tr key={r.rowId} style={{ borderBottom: "1px solid var(--line, #d2cbc1)" }}>
              <td style={tdStyle}><input type="checkbox" aria-label={`Select product ${r.shownCode}`} checked={selectedIds.has(String(r.block_id))} disabled={selectionDisabled} onChange={() => toggleSelection(String(r.block_id))} /></td>
              <td style={tdStyle}>{i + 1}</td>
              <td style={{ ...tdStyle, fontWeight: "bold" }}>{r.shownCode}</td>
              <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{r.entryDate || "—"}</td>
              <td style={tdStyle}>{r.productType}</td>
              <td style={tdStyle}>{r.customer}</td>
              <td style={tdStyle}>{r.material}</td>
              <td style={tdStyle}>{formatInventoryNumber(r.width)}</td>
              <td style={tdStyle}>{formatInventoryNumber(r.height)}</td>
              <td style={tdStyle}>{formatInventoryNumber(r.thickness)}</td>
              <td style={tdStyle}>{formatInventoryNumber(r.slabs)}</td>
              <td style={tdStyle}>{isInventorySoldOut(r) ? "Sold out" : formatInventoryNumber(r.area)}</td>
              <td style={tdStyle}>{r.price == null ? "Not priced" : formatInventoryNumber(r.price)}</td>
              <td style={tdStyle}>{r.treat}</td>
              <td className="ui-table-notes" style={tdStyle}>{r.notes || "-"}</td>
              <td style={{ ...tdStyle, whiteSpace: "normal" }}><div className="ui-actions">
                <button className="ui-primary"
                  disabled={selectionDisabled}
                  onClick={() => {
                    const b = blocks.find((x) => x.id === r.block_id);
                    if (b) {
                      setEditBlock(b);
                      setShowPopup(true);
                    }
                  }}
                  style={{
                    marginRight: 6,
                    padding: "4px 12px",
                    backgroundColor: "var(--accent, #514536)",
                    color: "#faf8f4",
                    border: "none",
                    borderRadius: 5,
                    cursor: "pointer",
                  }}
                >
                  Edit
                </button>
                <button className="ui-secondary"
                  disabled={selectionDisabled}
                  onClick={() => handleDelete([r.block_id])}
                  style={{
                    color: "#514536",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Delete
                </button>
              </div></td>
            </tr>
          ))}
          {!loading && visibleRows.length === 0 && (
            <tr>
              <td colSpan={16} style={{ textAlign: "center", color: "#716759" }}>
                {rows.length ? "No records match the current filters." : "No products yet"}
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
      </div>

      {showPopup && (
        <PopupModal onClose={() => { if (!formBusy) setShowPopup(false); }}>
          <BlockForm
            user={user}
            onBusyChange={setFormBusy}
            initialBlock={editBlock}
            onClose={() => setShowPopup(false)}
            onSaved={() => {
              setShowPopup(false);
              fetchAll();
            }}
            customers={customers}
            materials={materials}
            reloadMaterials={fetchAll}
          />
        </PopupModal>
      )}
    </div>
  );
}

export default BlockTable;
